import React, { useState } from "react";
import { Flex, Button, IconButton, Select, Text } from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import {
  FaCut,
  FaUndo,
  FaRedo,
  FaAlignRight,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignJustify,
} from "react-icons/fa";
import {
  FaMagnifyingGlassMinus,
  FaMagnifyingGlassPlus,
  FaRegPaste,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa6";
import {
  ANSWER_TEMPLATE,
  normalizeAnswerTemplate,
} from "../constants/answerTemplates";

const ToolBar = ({
  toggleSpecialChar,
  onZoomIn,
  onZoomOut,
  onScaleChange,
  scale,
  templateType,
  isPreviewMode,
}) => {
  const isMinistryTemplate =
    normalizeAnswerTemplate(templateType) ===
    ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE;
  const [isOpen, setIsOpen] = useState(true);

  const runEditorCommand = (command) => {
    document.execCommand(command);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      document.execCommand("insertText", false, text);
    } catch (err) {
      console.error("Failed to paste text: ", err);
    }
  };

  if (isMinistryTemplate) {
    const toolButtonStyle = {
      minW: "32px",
      width: "32px",
      height: "32px",
      padding: 0,
      margin: "0 2px",
      borderRadius: "4px",
      backgroundColor: "#87b8f0",
      color: "#212529",
      _hover: { backgroundColor: "#9fc7f5" },
      _active: { backgroundColor: "#bdd8f8" },
    };

    return (
      <Flex
        aria-label="법무부 답안 편집 도구"
        align="center"
        justify="center"
        position="fixed"
        top="60px"
        left="0"
        right="0"
        zIndex="998"
        height="40px"
        backgroundColor="#70a4dd"
        boxShadow="0 1px 2px rgba(0, 0, 0, 0.18)"
      >
        {!isPreviewMode && (
          <>
            <IconButton
              title="복사 : 글자를 복사합니다.(Ctrl+C)"
              aria-label="복사"
              icon={<CopyIcon />}
              onClick={() => runEditorCommand("copy")}
              {...toolButtonStyle}
            />
            <IconButton
              title="붙여넣기 : 복사한 내용을 붙여 넣습니다.(Ctrl+V)"
              aria-label="붙여넣기"
              icon={<FaRegPaste />}
              onClick={handlePaste}
              {...toolButtonStyle}
            />
            <IconButton
              title="되돌리기 : 입력한 정보를 원상태로 되돌립니다.(Ctrl+Z)"
              aria-label="되돌리기"
              icon={<FaUndo />}
              onClick={() => runEditorCommand("undo")}
              {...toolButtonStyle}
            />
            <IconButton
              title="다시 실행 : 되돌렸던 정보를 다시 실행합니다.(Ctrl+Y)"
              aria-label="다시 실행"
              icon={<FaRedo />}
              onClick={() => runEditorCommand("redo")}
              {...toolButtonStyle}
            />
            <IconButton
              title="왼쪽 정렬 : 내용을 왼쪽에 맞춥니다."
              aria-label="왼쪽 정렬"
              icon={<FaAlignLeft />}
              onClick={() => runEditorCommand("justifyLeft")}
              {...toolButtonStyle}
            />
            <IconButton
              title="중앙 정렬 : 내용을 중앙에 맞춥니다."
              aria-label="중앙 정렬"
              icon={<FaAlignCenter />}
              onClick={() => runEditorCommand("justifyCenter")}
              {...toolButtonStyle}
            />
            <IconButton
              title="오른쪽 정렬 : 내용을 오른쪽에 맞춥니다."
              aria-label="오른쪽 정렬"
              icon={<FaAlignRight />}
              onClick={() => runEditorCommand("justifyRight")}
              {...toolButtonStyle}
            />
            <IconButton
              title="양쪽 정렬 : 양쪽여백 사이 텍스트를 균일하게 분배합니다."
              aria-label="양쪽 정렬"
              icon={<FaAlignJustify />}
              onClick={() => runEditorCommand("justifyFull")}
              {...toolButtonStyle}
            />
          </>
        )}

        <Flex align="center" ml="18px">
          <Text as="strong" fontSize="14px" mr="6px" whiteSpace="nowrap">
            화면크기
          </Text>
          <Select
            aria-label="화면크기"
            value={Math.round(scale * 100)}
            onChange={(event) => onScaleChange(Number(event.target.value) / 100)}
            width="76px"
            height="32px"
            backgroundColor="white"
            borderColor="#4f7fb6"
            fontSize="14px"
            padding="0"
          >
            {[80, 100, 130, 150, 200].map((value) => (
              <option key={value} value={value}>
                {value}%
              </option>
            ))}
          </Select>
        </Flex>

        {!isPreviewMode && (
          <Button
            title="문자표 : 특수문자를 활용할 수 있습니다."
            aria-label="문자표"
            onClick={toggleSpecialChar}
            minW="100px"
            width="100px"
            height="32px"
            padding="0"
            margin="0 2px"
            ml="12px"
            borderRadius="4px"
            backgroundColor="#87b8f0"
            color="#212529"
            fontWeight="700"
            _hover={{ backgroundColor: "#9fc7f5" }}
          >
            ※ 문자표
          </Button>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      alignItems="center"
      justifyContents="center"
      direction="column"
      position="fixed"
      left="20px"
      top="110px"
      zIndex="1000"
      width="60px"
    >
      <IconButton
        aria-label={isOpen ? "접기" : "펼치기"}
        icon={isOpen ? <FaChevronUp /> : <FaChevronDown />}
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        size="sm"
        mb="4"
      />
      {isOpen && (
        <Flex
          direction="column"
          borderRadius="15px"
          height="60%"
          bg="white"
          p="4"
          border="1px solid gray.400"
        >
          {!isPreviewMode && (
            <>
              <IconButton mb="4" aria-label="실행 취소" icon={<FaUndo />} onClick={() => runEditorCommand("undo")} variant="ghost" size="sm" />
              <IconButton mb="4" aria-label="실행 취소의 취소" icon={<FaUndo />} onClick={() => runEditorCommand("redo")} sx={{ transform: "scaleX(-1)" }} variant="ghost" size="sm" />
              <IconButton mb="4" aria-label="왼쪽 정렬" icon={<FaAlignLeft />} onClick={() => runEditorCommand("justifyLeft")} variant="ghost" size="sm" />
              <IconButton mb="4" aria-label="중앙 정렬" icon={<FaAlignCenter />} onClick={() => runEditorCommand("justifyCenter")} variant="ghost" size="sm" />
              <IconButton mb="4" aria-label="오른쪽 정렬" icon={<FaAlignRight />} onClick={() => runEditorCommand("justifyRight")} variant="ghost" size="sm" />
              <IconButton mb="4" aria-label="복사" icon={<CopyIcon />} onClick={() => runEditorCommand("copy")} variant="ghost" size="sm" />
              <IconButton mb="4" aria-label="오리기" icon={<FaCut />} onClick={() => runEditorCommand("cut")} variant="ghost" size="sm" />
              <IconButton mb="4" aria-label="붙여넣기" icon={<FaRegPaste />} onClick={handlePaste} variant="ghost" size="sm" />
              <Button mb="4" onClick={toggleSpecialChar} variant="ghost" size="sm">기호</Button>
            </>
          )}
          <IconButton mb="4" aria-label="확대" icon={<FaMagnifyingGlassPlus />} onClick={onZoomIn} variant="ghost" size="sm" />
          <IconButton mb="4" aria-label="축소" icon={<FaMagnifyingGlassMinus />} onClick={onZoomOut} variant="ghost" size="sm" />
        </Flex>
      )}
    </Flex>
  );
};

export default ToolBar;
